// Passing Profile — a "wagon wheel" of a player's passes, every pass re-centred on one
// origin by its real direction and length. Ported from js/renderers-passingprofile.js
// (renderPassingProfileModule, drawWheel, wireMagneticWheelHover, wireWedgeInteractions).
//
// needsPlayer:true, not mergeable. It has its own small copy of the event-module shell (as
// the legacy does — no Table view, no zoom), built from the shared kit UI: comp/season strip,
// stats bar, chips + Colour-by (`_wheelColorBy`, its own key so it can differ from a Pass Map
// of the same player). Reads `_`-state every render (no rebuildOnRefresh).
//
// - Lines: the Pass Map pattern — memoised JSX, ONE effect for the magnetic hover by direct
//   class toggles + the tooltip. Each line also keeps its declarative data-tooltip.
// - Wedges: click selects one (`_selectedWedge`); the chips then filter WITHIN that wedge.
//   A click away clears it (04l-f fork 1A: this module's own document listener, with the
//   legacy's exclusions). The legacy's single global listener usually cleared first until
//   renderers-passingprofile.js was deleted (05k-2); ours has done it alone since.
// - Hovering a wedge previews its stats in the bar. That preview is state inside the bar
//   component (fork 2A), so a wedge hover is NOT a render of this module — which keeps the
//   selection re-broadcast below exactly "once per real render", as the legacy.
// - The link: we broadcast our hovered pass and highlight another widget's (catalyst:event-
//   highlight); a wedge click broadcasts its pass ids (catalyst:event-selection), and every
//   render re-sends them while a wedge is selected, so an open Pass Map never shows a stale
//   group after a filter / season / player change.
//
// Two sources, one body (as the Shot / Pass Map shell): the snapshot, or on ?live=1 any
// player's real passes (T2-9, hooks/useLiveEventMap.ts).

import { focusEmptyText, inFocus, moduleFocus } from "../live/event-focus.js";
import { FocusChip } from "./FocusChip.js";
import { LinkedChip } from "./LinkedChip.js";
import { useLinkedSelection } from "./useLinkedSelection.js";
import { ModuleExplainer } from "./ModuleExplainer.js";
import { eventChipsOf, explainPassingProfile } from "./explain.js";
import { humanizeSeasonCode } from "./league-table-view.js";
import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type RefObject } from "react";
import type { ProtoEvent, ProtoShotPassDetail } from "@catbento/shared";
import { type ProtoModuleProps, getModState, setModState } from "./bridge.js";
import { liveShortName, useGradientPrefix, useRefresh, type EventMapData } from "./EventMapBody.js";
import { EventSubControls } from "./EventSubControls.js";
import { EventStatsBar } from "./EventStatsBar.js";
import { EventFilterBar } from "./EventFilterBar.js";
import { noDataMessage } from "./no-data.js";
import { hoverLineFlags, passiveLineFlags, resolvePassiveState } from "./passmap-view.js";
import {
  CENTER,
  CHART_SIZE,
  COMPASS_LABELS,
  computeWedgeStats,
  compassLabelStyle,
  nextHoveredWheelLine,
  passWedgeDeg,
  ringTickLabels,
  wedgeBucket,
  wedgePreviewStats,
  wheelFrame,
  wheelLine,
  wheelStats,
  type WedgeBucket,
  type WheelLine,
  type WheelStats,
} from "./passingprofile-view.js";
import {
  PASS_COLOR_MODES,
  matchesEventFilters,
  paintOrder,
  passFilters,
  pruneStaleEventFilters,
  resolveColorMode,
  resolveEventState,
  type EventActive,
  type EventColorMode,
  type EventContext,
} from "../engines/event-map-kit.js";
import {
  broadcastEventHighlight,
  broadcastEventLock,
  lockedEvent,
  subscribeEventHighlight,
  subscribeEventLock,
  releasePasses,
  selectPasses,
} from "../engines/event-link.js";
import { readSnapshot } from "../data/snapshot.js";
import { getPlayerShortName } from "../data/player-utils.js";
import { canvasApi } from "../canvas/shell-actions.js";
import { useEventContext } from "../hooks/useEventContext.js";
import { useShotPassEvents } from "../hooks/useShotPassEvents.js";
import { useLiveEventMap } from "../hooks/useLiveEventMap.js";
import { EVENT_KINDS } from "../live/event-map.js";
import { LIVE } from "../live/flag.js";
import { hideTooltip, showTooltipAt } from "../canvas/tooltip.js";

const EMPTY_EVENTS: ProtoEvent[] = [];
/** A wedge's words for the shared selection's chip ("Forward-right passes"); wedges sit between the
 *  compass points, clockwise from forward. */
const WEDGE_NAMES = ["Forward-right", "Right-forward", "Right-back", "Back-right", "Back-left", "Left-back", "Left-forward", "Forward-left"];
function wedgeLabel(deg: number): string {
  return `${WEDGE_NAMES[Math.floor(deg / 45) % 8]} passes`;
}
const COLOR_MODE_LABELS = PASS_COLOR_MODES.map((m) => m.label);
const FRAME = wheelFrame();
const RING_TICKS = ringTickLabels();
/** A click on any of these is a real action, not "clicking away" — picking a filter value
 *  inside a wedge must not clear the wedge (the legacy's own bug fix). The two popovers live
 *  on document.body, so a plain "inside this module" check wouldn't catch them. */
const KEEPS_WEDGE = ".pp-hover-wedge, .event-filter-bar, .value-picker, .filter-range-picker";

/** Hookless dispatch, so each source keeps its own hook order. Live: any player in the DB,
 *  by id. Snapshot: an unknown name → the first player (the legacy's fallback), so the
 *  body's hooks always see a real player. */
export function PassingProfileModule(props: ProtoModuleProps) {
  if (LIVE) return <LivePassingProfile {...props} />;
  const players = readSnapshot().players;
  const name = props.playerName && players[props.playerName] ? props.playerName : Object.keys(players)[0];
  const p = name ? players[name] : undefined;
  if (!p || !name) return <div className="stats-empty">No data.</div>;
  return <SnapshotPassingProfile {...props} eventsId={Number(p.id)} shortName={getPlayerShortName(p)} emptySeason={p.currentSeason ?? null} />;
}

/** `eventsId` = the shown player's id (her events are keyed by it), after the fallback. */
function SnapshotPassingProfile(props: ProtoModuleProps & { eventsId: number; shortName: string; emptySeason: string | null }) {
  const { mod } = props;
  // The pickers' re-render must reach this parent, which resolves the pick (EventMapBody).
  const refresh = useRefresh();
  const ctx = useEventContext();
  const spd: ProtoShotPassDetail | null = useShotPassEvents(props.eventsId);
  // Resolve comp/season against THIS player's data, then write the resolved ids back.
  const state = resolveEventState(
    {
      compId: getModState<string>(mod, "_eventMapCompetitionId"),
      seasonCode: getModState<string>(mod, "_eventMapSeason"),
    },
    spd,
  );
  if (state) {
    setModState(mod, "_eventMapCompetitionId", state.compId);
    setModState(mod, "_eventMapSeason", state.seasonCode);
  }
  return <PassingProfileBody {...props} data={{ spd, state, ctx }} refresh={refresh} />;
}

function LivePassingProfile(props: ProtoModuleProps) {
  const refresh = useRefresh();
  const data = useLiveEventMap(props.mod, props.playerId, EVENT_KINDS.passes);
  return <PassingProfileBody {...props} shortName={liveShortName(props)} data={data} refresh={refresh} />;
}

function PassingProfileBody({
  mod,
  body,
  playerId,
  shortName,
  emptySeason,
  data: { spd, state, ctx },
  refresh: forceRender,
}: ProtoModuleProps & { shortName: string; data: EventMapData; refresh: () => void;
  /** The season the "no data" message names when she has no events at all (snapshot: her
   *  current one). Live: unknown → the message stops after her name. */
  emptySeason?: string | null }) {
  const [filterVersion, setFilterVersion] = useState(0);
  const bumpFilters = () => setFilterVersion((v) => v + 1);
  // The bar's preview setter — a ref, so a wedge hover never re-renders this module.
  const previewRef = useRef<((bucket: WedgeBucket | null) => void) | null>(null);

  useEffect(() => {
    body.style.justifyContent = "flex-start";
  }, [body]);

  const defs = useMemo(() => passFilters(ctx), [ctx]);
  const seasonPasses = state ? state.season.passes : EMPTY_EVENTS;
  // Live: a match dragged in from Matches narrows the wheel to that game (Ben, 2026-10-18; event-focus.ts).
  const focus = LIVE ? moduleFocus(mod, playerId) : null;
  const passes = useMemo(() => (focus ? seasonPasses.filter((e) => inFocus(e, focus)) : seasonPasses), [seasonPasses, focus]);
  const focusChip = focus ? <FocusChip mod={mod} focus={focus} /> : null;
  const focusChipInline = focus ? <FocusChip mod={mod} focus={focus} inline /> : null;
  // Seeded before the no-passes bail-out (the legacy's order here, unlike Pass Map's).
  if (state && !getModState<EventActive>(mod, "_eventFilters")) setModState(mod, "_eventFilters", {});
  const active = getModState<EventActive>(mod, "_eventFilters");
  const selectedWedge = getModState<number | null>(mod, "_selectedWedge") ?? null;
  // The shared selection (Ben, 2026-10-21): the other modules' picks → only their passes stay bright and
  // hoverable here, and the numbers count only them (a blue chip each); they add to this wheel's own
  // wedge (2026-10-26), which goes only when this wheel's pick is ✕'d elsewhere.
  const linked = useLinkedSelection(mod, passes, () => {
    if (getModState<number | null>(mod, "_selectedWedge") != null) {
      setModState(mod, "_selectedWedge", null);
      forceRender();
    }
  });

  // The selected wedge is the BASE the chips work within: options, pruning and the match
  // are all computed against it, so a value that doesn't occur inside it isn't offered.
  const segmentPasses = useMemo(
    () => (selectedWedge != null ? passes.filter((ps) => passWedgeDeg(ps) === selectedWedge) : passes),
    [passes, selectedWedge],
  );
  if (state && active) pruneStaleEventFilters(active, segmentPasses, defs);
  const filtered = useMemo(
    () => (active ? segmentPasses.filter((e) => matchesEventFilters(e, active, defs)) : EMPTY_EVENTS),
    // filterVersion stands in for the in-place edits to `active`.
    [segmentPasses, active, defs, filterVersion],
  );
  // A wedge hover previews "select this one INSTEAD", so its buckets use the chips over the
  // whole season — not the current segment, which would make every other wedge read empty.
  const wholeSeasonFiltered = useMemo(
    () => (active ? passes.filter((e) => matchesEventFilters(e, active, defs)) : EMPTY_EVENTS),
    [passes, active, defs, filterVersion],
  );
  const buckets = useMemo(() => computeWedgeStats(wholeSeasonFiltered), [wholeSeasonFiltered]);
  // This wheel's pick for the shared selection (Ben, 2026-10-21): the picked direction and / or its
  // filter chips ("Recipient: Ben White"). Announced when the user changes either — never on a
  // redraw. Its own passes only: the others' picks are layered on by whoever shows them.
  const announce = () => {
    if (!LIVE || !active) return;
    const wedge = getModState<number | null>(mod, "_selectedWedge") ?? null;
    const chips = eventChipsOf(active, defs).map((c) => `${c.label}: ${c.value}`);
    if (wedge == null && chips.length === 0) {
      releasePasses(mod);
      return;
    }
    const ids = passes
      .filter((ps) => (wedge == null || passWedgeDeg(ps) === wedge) && matchesEventFilters(ps, active, defs))
      .map((ps) => ps.id);
    selectPasses({ ids, label: [wedge != null ? wedgeLabel(wedge) : null, ...chips].filter(Boolean).join(", "), source: mod });
  };
  // The click-away listener is wired once, so it reads the latest announce through a ref.
  const announceRef = useRef(announce);
  announceRef.current = announce;

  // A render also resets a wedge-hover preview, as the legacy's rebuilt bar did.
  useEffect(() => {
    previewRef.current?.(null);
  });

  // Click away → clear this module's wedge (fork 1A).
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if ((e.target as Element | null)?.closest?.(KEEPS_WEDGE)) return;
      if (getModState<number | null>(mod, "_selectedWedge") == null) return;
      setModState(mod, "_selectedWedge", null);
      announceRef.current();
      canvasApi().refreshModuleBody(mod);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [mod]);

  const subControls = (
    <EventSubControls
      mod={mod}
      spd={spd}
      state={state}
      onCompetitionChange={(compId) => {
        setModState(mod, "_eventMapCompetitionId", compId);
        // Deleted, not set to undefined — as the legacy does, so saveState captures no key.
        Reflect.deleteProperty(mod, "_eventMapSeason");
        forceRender();
      }}
      onSeasonChange={(seasonCode) => {
        setModState(mod, "_eventMapSeason", seasonCode);
        forceRender();
      }}
    />
  );

  if (!spd || !state || !active) {
    return (
      <>
        {subControls}
        <EventStatsBar mod={mod} body={body} hidden />
        {/* Ben's one message (Q-012). No season: she has no events in any. */}
        <div className="stats-empty">{noDataMessage("pass", shortName, state?.seasonCode ?? emptySeason ?? null)}</div>
      </>
    );
  }

  if (!passes.length) {
    return (
      <>
        {subControls}
        <EventStatsBar mod={mod} body={body} hidden />
        {focus && seasonPasses.length ? (
          <>
            {focusChip}
            <div className="stats-empty">{focusEmptyText(shortName, "passes", focus)}</div>
          </>
        ) : (
          <div className="stats-empty">{noDataMessage("pass", shortName, state.seasonCode)}</div>
        )}
      </>
    );
  }

  // Seeded here, as the legacy's wireColorByChip does on every wheel render.
  if (!getModState<string>(mod, "_wheelColorBy")) setModState(mod, "_wheelColorBy", "result");
  const colorMode = resolveColorMode(PASS_COLOR_MODES, getModState<string>(mod, "_wheelColorBy"));

  const pickWedge = (deg: number) => {
    const bucket = wedgeBucket(buckets, deg);
    if (!bucket || !bucket.count) return;
    setModState(mod, "_selectedWedge", deg);
    // The shared selection: the Pass Map and Passing Report follow the wedge's passes (and chips).
    announce();
    forceRender();
  };

  // A blank wheel (chips still in place) at zero matches — the empty chart says it.
  return (
    <>
      {LIVE && (
        <ModuleExplainer
          mod={mod}
          paragraphs={explainPassingProfile({
            name: shortName,
            competition: state.comp.name,
            season: humanizeSeasonCode(state.seasonCode),
            total: passes.length,
            shown: wholeSeasonFiltered.length,
            chips: eventChipsOf(active, defs),
            focus,
            colour: colorMode.label,
            wedge: selectedWedge != null,
          })}
        />
      )}
      {subControls}
      <WheelStatsBar
        mod={mod}
        body={body}
        resting={wheelStats(linked ? filtered.filter((e) => linked.ids.has(e.id)) : filtered, segmentPasses)}
        totalShown={wholeSeasonFiltered.length}
        previewRef={previewRef}
      />
      <div className="passing-profile-wrap">
        <EventFilterBar
          active={active}
          events={segmentPasses}
          defs={defs}
          onChange={() => {
            bumpFilters();
            announce();
          }}
          extra={
            <>
              {focusChipInline}
              {linked && <LinkedChip linked={linked} />}
            </>
          }
          colorBy={{
            options: COLOR_MODE_LABELS,
            current: colorMode.label,
            onPick: (label) => {
              const picked = PASS_COLOR_MODES.find((m) => m.label === label);
              if (picked) setModState(mod, "_wheelColorBy", picked.key);
              forceRender();
            },
          }}
        />
        <Wheel
          ctx={ctx}
          passes={filtered}
          colorMode={colorMode}
          selectedWedge={selectedWedge}
          buckets={buckets}
          previewRef={previewRef}
          onPickWedge={pickWedge}
          linkedIds={linked?.ids ?? null}
        />
      </div>
    </>
  );
}

// --- the stats bar (owns the wedge-hover preview) --------------------------------------------

function WheelStatsBar({
  mod,
  body,
  resting,
  totalShown,
  previewRef,
}: {
  mod: HTMLElement;
  body: HTMLElement;
  resting: WheelStats;
  totalShown: number;
  previewRef: RefObject<((bucket: WedgeBucket | null) => void) | null>;
}) {
  const [preview, setPreview] = useState<WedgeBucket | null>(null);
  useEffect(() => {
    previewRef.current = setPreview;
    return () => {
      previewRef.current = null;
    };
  }, [previewRef]);
  const s = preview ? wedgePreviewStats(preview, totalShown) : resting;
  return (
    <EventStatsBar mod={mod} body={body} hidden={false}>
      <div className="event-stat"><span className="event-stat-value">{s.shown}</span><span className="event-stat-label">passes shown</span></div>
      <div className="event-stat"><span className="event-stat-value">{s.success}</span><span className="event-stat-label">success</span></div>
      <div className="event-stat"><span className="event-stat-value">{s.avgLength}</span><span className="event-stat-label">avg length</span></div>
    </EventStatsBar>
  );
}

// --- the wheel --------------------------------------------------------------------------------

function Wheel({
  ctx,
  passes,
  colorMode,
  selectedWedge,
  buckets,
  previewRef,
  onPickWedge,
  linkedIds,
}: {
  ctx: EventContext;
  passes: ProtoEvent[];
  colorMode: EventColorMode;
  selectedWedge: number | null;
  buckets: WedgeBucket[];
  previewRef: RefObject<((bucket: WedgeBucket | null) => void) | null>;
  onPickWedge: (deg: number) => void;
  /** Another module's picked passes: only these stay bright and hoverable (null = all). */
  linkedIds: ReadonlySet<string> | null;
}) {
  const gradPrefix = useGradientPrefix();
  const svgRef = useRef<SVGSVGElement>(null);

  // Extreme values paint last (on top) for a continuous mode; a pass without an angle or
  // length isn't drawn.
  const lines = useMemo(
    () => paintOrder(passes, colorMode).map((ps) => wheelLine(ctx, ps, colorMode)).filter((l): l is WheelLine => l !== null),
    [passes, colorMode, ctx],
  );
  // Memoised elements: React skips them on a re-render, so their hover classes stay the
  // effect's.
  const gradients = useMemo(
    () =>
      lines.map((ld, i) => (
        <linearGradient key={ld.pass.id} id={`${gradPrefix}-${i}`} gradientUnits="userSpaceOnUse" x1={ld.x1} y1={ld.y1} x2={ld.x2} y2={ld.y2}>
          <stop offset="0%" stopColor={ld.rgb} stopOpacity="0.04"></stop>
          <stop offset="100%" stopColor={ld.rgb} stopOpacity="0.75"></stop>
        </linearGradient>
      )),
    [lines, gradPrefix],
  );
  const lineEls = useMemo(
    () =>
      lines.map((ld, i) => (
        <line
          key={ld.pass.id}
          x1={ld.x1}
          y1={ld.y1}
          x2={ld.x2}
          y2={ld.y2}
          className={`pp-wheel-line${linkedIds && !linkedIds.has(ld.pass.id) ? " pp-wheel-line-out" : ""}`}
          stroke={`url(#${gradPrefix}-${i})`}
          data-tooltip={ld.tooltip}
        ></line>
      )),
    [lines, gradPrefix, linkedIds],
  );

  useEffect(() => {
    const svg = svgRef.current;
    const layer = svg?.querySelector(".pp-spokes-layer");
    if (!svg || !layer) return;
    // React renders the lines in `lines` order, so child i is line i.
    const all = lines.map((ld, i) => ({ ...ld, el: layer.children[i] as SVGLineElement }));
    // Another module's pick: only its passes can be hovered or clicked (the rest are faded).
    const entries = linkedIds ? all.filter((e) => linkedIds.has(e.pass.id)) : all;
    const byId = new Map(all.map((e) => [e.pass.id, e]));
    let selected: (typeof entries)[number] | null = null;
    // A clicked pass (event-link's lock): it holds the highlight, and hover waits until it's cleared.
    let locked: (typeof entries)[number] | null = null;

    const applyFlags = (flagsFor: (id: string) => { selected: boolean; dimmed: boolean }) => {
      for (const e of entries) {
        const f = flagsFor(e.pass.id);
        e.el.classList.toggle("pp-wheel-line-selected", f.selected);
        e.el.classList.toggle("pp-wheel-line-dimmed", f.dimmed);
      }
    };
    const showLock = (id: string | null) => {
      locked = id === null ? null : (byId.get(id) ?? null);
      selected = null;
      hideTooltip();
      applyFlags(locked ? (x) => hoverLineFlags(x, locked!.pass.id) : () => ({ selected: false, dimmed: false }));
    };
    showLock(lockedEvent());
    const setSelected = (entry: typeof selected, e?: MouseEvent) => {
      if (locked) return;
      if (selected !== entry) {
        selected = entry;
        applyFlags(entry ? (id) => hoverLineFlags(id, entry.pass.id) : () => ({ selected: false, dimmed: false }));
        // A Pass Map (or another wheel) of this player highlights the same pass.
        broadcastEventHighlight(entry ? entry.pass.id : null);
      }
      if (entry && e) showTooltipAt(entry.tooltip, e.clientX, e.clientY);
      else hideTooltip();
    };

    // Receiving half — purely visual (no tooltip: a broadcast carries no cursor on this
    // chart). Skipped while our own hover owns the classes (incl. our own echo).
    const offHighlight = subscribeEventHighlight((id) => {
      if (selected || locked) return;
      const st = resolvePassiveState(id, null, (x) => byId.has(x));
      applyFlags((x) => passiveLineFlags(x, st));
    });

    const onMove = (e: MouseEvent) => {
      const ctm = svg.getScreenCTM();
      if (!ctm) return;
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      setSelected(nextHoveredWheelLine(pt.matrixTransform(ctm.inverse()), entries, selected), e);
    };
    const onLeave = () => setSelected(null);
    // Click a pass → it's selected here and on every open Pass Map (Ben, 2026-10-20); the same pass
    // again, or anywhere while one is selected, clears it. A click with no pass near still picks
    // the wedge. Caught before the wedge sees it.
    const onClick = (e: MouseEvent) => {
      const ctm = svg.getScreenCTM();
      if (!ctm) return;
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const hit = nextHoveredWheelLine(pt.matrixTransform(ctm.inverse()), entries, null);
      if (!hit && !locked) return;
      e.stopPropagation();
      broadcastEventHighlight(null);
      broadcastEventLock(hit && hit !== locked ? hit.pass.id : null);
    };
    const offLock = subscribeEventLock(showLock);

    svg.addEventListener("mousemove", onMove);
    svg.addEventListener("mouseleave", onLeave);
    svg.addEventListener("click", onClick, true);
    return () => {
      svg.removeEventListener("mousemove", onMove);
      svg.removeEventListener("mouseleave", onLeave);
      svg.removeEventListener("click", onClick, true);
      offHighlight();
      offLock();
      // React reuses the same <line> nodes across a re-draw — drop our classes first.
      for (const e of entries) e.el.classList.remove("pp-wheel-line-selected", "pp-wheel-line-dimmed");
    };
  }, [lines, linkedIds]);

  const previewWedge = (deg: number) => {
    const bucket = wedgeBucket(buckets, deg);
    if (bucket && bucket.count) previewRef.current?.(bucket);
  };
  const compassTooltip = (text: string) => (e: ReactMouseEvent) => showTooltipAt(text, e.clientX, e.clientY);

  return (
    <div className="pp-chart-inner">
      <svg ref={svgRef} className="pp-wheel-svg" viewBox={`0 0 ${CHART_SIZE} ${CHART_SIZE}`} preserveAspectRatio="xMidYMid meet">
        <defs className="pp-defs">{gradients}</defs>
        <g className="pp-rings">
          {FRAME.rings.map((r) => (
            <circle key={r} className="pp-ring" cx={CENTER} cy={CENTER} r={r}></circle>
          ))}
        </g>
        <g className="pp-ref-spokes-layer">
          {FRAME.refSpokes.map((s, i) => (
            <line key={i} className="pp-ref-spoke" x1={CENTER} y1={CENTER} x2={s.x2} y2={s.y2}></line>
          ))}
        </g>
        {/* Invisible until hovered (CSS). Hover previews the wedge's stats; click selects it. */}
        <g className="pp-wedge-hit-layer">
          {FRAME.wedges.map((w) => (
            <path
              key={w.deg}
              className={`pp-hover-wedge${selectedWedge === w.deg ? " active" : ""}`}
              data-deg={w.deg}
              d={w.d}
              onMouseEnter={() => previewWedge(w.deg)}
              // The legacy rebuilt the wedge under a still cursor, so its next move fired a
              // fresh mouseenter; ours is the same node, so a move re-applies the preview
              // after a render has reset it (a no-op otherwise — same bucket object).
              onMouseMove={() => previewWedge(w.deg)}
              onMouseLeave={() => previewRef.current?.(null)}
              onClick={() => onPickWedge(w.deg)}
            ></path>
          ))}
        </g>
        <g className="pp-spokes-layer">{lineEls}</g>
        <circle className="pp-center-dot" cx={CENTER} cy={CENTER} r="3"></circle>
      </svg>
      <div className="pp-labels-overlay">
        {COMPASS_LABELS.map((c) => (
          <span
            key={c.deg}
            className="pp-compass-label"
            style={compassLabelStyle(c.deg)}
            data-tooltip={c.tooltip}
            onMouseEnter={compassTooltip(c.tooltip)}
            onMouseMove={compassTooltip(c.tooltip)}
            onMouseLeave={() => hideTooltip()}
          >
            {c.glyph}
          </span>
        ))}
        {RING_TICKS.map((t) => (
          <span key={t.yd} className="pp-ring-tick-label" style={{ left: "50%", top: `${t.topPct}%` }}>
            {t.yd}yd
          </span>
        ))}
      </div>
    </div>
  );
}
